import { EMPTY_PROFILE, parseProgress, parseStatus } from "./demo/helpers";
import type { SeedingProfile } from "./demo/helpers";
import type { OnboardingKind, OnboardingWire } from "./types";

export interface DecodedOnboarding {
  readonly kind: OnboardingKind | null;
  readonly completed: boolean;
  readonly seeding: SeedingProfile;
}

/**
 * Nobody is signed in, so there is nobody to onboard.
 *
 * Reached only from the no-token path in `useDemoSeeding` — never as a fallback
 * for a profile that failed to describe its user. A response the decoder cannot
 * read throws, because silently calling an unreadable profile "onboarded" is
 * how a half-seeded workspace would get opened.
 */
export const EMPTY_ONBOARDING: DecodedOnboarding = {
  kind: null,
  completed: true,
  seeding: EMPTY_PROFILE,
};

function parseKind(value: unknown): OnboardingKind {
  if (value === "demo" || value === "full") return value;
  throw new Error("invalid onboarding kind");
}

/** Seed state, present only for a demo user with something to report. */
function parseDemo(value: unknown): SeedingProfile {
  if (value === undefined || value === null) return EMPTY_PROFILE;
  if (typeof value !== "object") throw new Error("invalid demo onboarding");
  const wire = value as { readonly status?: unknown; readonly progress?: unknown };
  const status = parseStatus(wire.status);
  if (status === null) return EMPTY_PROFILE;
  const progress = parseProgress(wire.progress);
  if (status === "provisioning" && progress === null) {
    throw new Error("a running seed must report progress");
  }
  return { status, progress };
}

/**
 * Decode `/api/me/profile` into the one thing a client needs: which onboarding
 * this user gets and whether they still owe it.
 *
 * Every authenticated profile carries the block — the server serializes it
 * unconditionally. A response without it is a contract violation, not a user
 * who happens to be onboarded, so it throws and the gate keeps whatever it
 * already knew rather than opening on a guess.
 */
export function decodeOnboarding(value: unknown): DecodedOnboarding {
  if (typeof value !== "object" || value === null) {
    throw new Error("invalid profile response");
  }
  const block = (value as { readonly onboarding?: unknown }).onboarding;
  if (block === undefined || block === null) {
    throw new Error("profile response carries no onboarding block");
  }
  if (typeof block !== "object") throw new Error("invalid onboarding block");
  const wire = block as OnboardingWire;
  if (typeof wire.completed !== "boolean") {
    throw new Error("invalid onboarding completion");
  }
  return {
    kind: parseKind(wire.kind),
    completed: wire.completed,
    seeding: parseDemo(wire.demo),
  };
}
